import * as React from "react";
import { z } from "zod";
import { AssetSchema } from "@forgepoint/shared";
import { fetchApi } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { AssetPreviewDialog } from "@/components/AssetPreviewDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { FileText, Loader2, Upload } from "lucide-react";

const GetAssetsByIdsResponseSchema = z.object({
  assets: z.array(AssetSchema),
});

const UploadAssetResponseSchema = z.object({
  uploadUrl: z.string(),
  asset: AssetSchema,
});

interface AssetSelectorFieldProps {
  value?: string;
  disabled?: boolean;
  onChange: (assetId: string) => void;
  onClickAsset?: (assetId: string) => void;
}

export function AssetSelectorField({
  value,
  disabled = false,
  onChange,
  onClickAsset,
}: AssetSelectorFieldProps) {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = React.useState(false);
  const [asset, setAsset] = React.useState<z.infer<typeof AssetSchema> | null>(
    null,
  );
  const [previewAssetId, setPreviewAssetId] = React.useState<string | null>(
    null,
  );

  const loadAsset = React.useCallback(async (assetId: string) => {
    const response = await fetchApi(`api/assets?ids=${assetId}`, "GET");
    if (!response.ok) {
      setAsset(null);
      return;
    }
    const parsed = GetAssetsByIdsResponseSchema.safeParse(response.data);
    if (!parsed.success) {
      setAsset(null);
      return;
    }
    setAsset(parsed.data.assets[0] ?? null);
  }, []);

  React.useEffect(() => {
    if (!value) {
      setAsset(null);
      return;
    }
    loadAsset(value);
  }, [loadAsset, value]);

  const handlePickFile = () => {
    if (disabled || isUploading) {
      return;
    }
    inputRef.current?.click();
  };

  const handleUpload = async (file: File) => {
    if (disabled || isUploading) {
      return;
    }
    setIsUploading(true);
    try {
      const response = await fetchApi("api/assets/upload", "POST", {
        body: {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
        },
      });

      if (!response.ok) {
        return;
      }

      const parsed = UploadAssetResponseSchema.safeParse(response.data);
      if (!parsed.success) {
        return;
      }

      await fetch(parsed.data.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });

      onChange(parsed.data.asset.id);
      setAsset(parsed.data.asset);
    } finally {
      setIsUploading(false);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  const handleFileInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    await handleUpload(file);
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (disabled || isUploading) {
      return;
    }

    const file = event.dataTransfer.files?.[0];
    if (!file) {
      return;
    }
    await handleUpload(file);
  };

  const handleAssetClick = () => {
    if (!asset) {
      return;
    }
    if (onClickAsset) {
      onClickAsset(asset.id);
      return;
    }
    setPreviewAssetId(asset.id);
  };

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={handleFileInputChange}
        disabled={disabled || isUploading}
        accept="image/*,application/pdf"
      />

      {asset && (
        <>
          <div className="flex min-w-0 items-center gap-2 rounded-md border border-border/60 bg-muted px-2 py-1.5">
            <button
              type="button"
              onClick={handleAssetClick}
              disabled={disabled}
              className="flex min-w-0 flex-1 items-center gap-1.5 text-left hover:bg-muted/70 rounded-md px-1 py-0.5 transition"
            >
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 truncate text-sm font-medium">
                {asset.fileName ?? asset.id}
              </span>
            </button>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    disabled={disabled || isUploading}
                    onClick={handlePickFile}
                  >
                    <Upload className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Replace file</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <AssetPreviewDialog
            assetId={previewAssetId}
            fileName={asset.fileName}
            onClose={() => setPreviewAssetId(null)}
          />
        </>
      )}

      {!asset && (
        <div
          className={cn(
            "border-2 border-dashed rounded-lg p-8 text-center flex flex-col items-center justify-center gap-4 transition-colors w-full",
            "hover:bg-muted/50 cursor-pointer",
            (disabled || isUploading) && "opacity-50 pointer-events-none",
          )}
          onClick={handlePickFile}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={handleDrop}
        >
          {isUploading ? (
            <>
              <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
              <p className="text-muted-foreground">Uploading file...</p>
            </>
          ) : (
            <>
              <Upload className="h-10 w-10 text-muted-foreground" />
              <div>
                <p className="font-medium">Upload file</p>
                <p className="text-sm text-muted-foreground">
                  Click to browse or drag & drop
                </p>
              </div>
              <Button variant="outline" size="sm" type="button">
                Select File
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
